// Copyright (c) Move Closer 2022.

import { Component, Prop, ProvideReactive, Vue } from 'vue-property-decorator'
import { ContainerComponent, ContentStructure, ModuleComponent, Viewer, ViewerConfig } from '@movecloser/page-builder'
import { difference } from 'lodash'
import { Route } from 'vue-router'

import {
  AllowedStructureBreakpoint,
  AllowedStructureSlot,
  containerUiComponent,
  ContentData, Inject,
  logger,
  PARENT_CONTENT_DATA_KEY,
  RegistryResponse,
  UI_CONTAINER_MOUNTED_PROVIDER_KEY
} from '@core'

import { ContentPreviousRoute } from '../contracts'
import { Module } from '../components'
import { EventbusType, IEventbus } from '@movecloser/front-core'
import WindowScrollMixin from '@/shared/core/src/front/shared/mixins/windowScroll.mixin'

/**
 * **ONE OF THE MOST IMPORTANT CLASS IN THE WHOLE APP.**
 *
 * ➡️ It's being rendered under the "catch-all" route.
 * ➡️ Before it gets mounted, it tries to fetch the page's data from the API, using the URL
 * entered by the User. If the API call is a success, then the content's modules are rendered.
 * Of course, if the API call has failed, the non-abstract component take care of it.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@Component<AbstractView>({
  components: { Viewer },

  metaInfo () {
    return this.result && this.result.content
      ? {
        link: [
          {
            rel: 'canonical',
            href: !!this.result.canonical && this.result.canonical.length > 0 ? this.result.canonical : this.$route.path
          }
        ],
        meta: this.result.meta,
        title: this.result.content.title
      } : {}
  },

  mounted (): void {
    this.isMounted = true
    this.eventBus.emit(
      'app:page.loaded',
      {
        title: this.result?.content.title,
        pagePath: this.result?.content.urlPath,
        language: this.$i18n.locale,
        turnOffPageViewForSPA: 1
      }
    )
  },

  errorCaptured (err: Error, vm: Vue, info: string): boolean | void {
    if (process.env.NODE_ENV === 'production') {
      logger([err, info], 'error')
      return false
    }
  }
})
export class AbstractView extends WindowScrollMixin {
  @Prop({ type: Boolean, required: false, default: false })
  public isLoggedIn!: boolean

  @Prop({ type: Boolean, required: false, default: false })
  public isWaitingForAuth!: boolean

  @Inject(EventbusType)
  protected readonly eventBus!: IEventbus

  protected allowedQueryParams: string[] = []
  public readonly config: ViewerConfig = { columns: 12 }

  public readonly containerComponent: ContainerComponent = containerUiComponent

  @ProvideReactive(UI_CONTAINER_MOUNTED_PROVIDER_KEY)
  public isMounted: boolean = false

  public readonly moduleComponent: ModuleComponent = Module
  public readonly slots = AllowedStructureSlot

  /**
   * The `LayoutBreakpoint` that should be used by the `<Viewer>` component
   * in order to display the page's content using the correct layout.
   */
  public get breakpoint (): AllowedStructureBreakpoint {
    return this.$store.getters.isMobile ? AllowedStructureBreakpoint.XS
      : AllowedStructureBreakpoint.LG
  }

  @ProvideReactive(PARENT_CONTENT_DATA_KEY)
  protected get parent (): ContentData | undefined {
    return this.result?.content
  }

  public get result (): RegistryResponse | null {
    return this.$store.getters['content/response']
  }

  /**
   * Determine if given slot has anything to display.
   */
  public hasStructureToDisplay (slot: string): boolean {
    return Object.values(this.resolveSlot(slot)).length > 0
  }

  /**
   * Resolve given slot of content.
   */
  public resolveSlot (slot: string): ContentStructure {
    if (!this.result) {
      return {}
    }

    return this.result.content.slots[slot] || {}
  }

  /**
   * Determine whether the page should be fully re-render.
   */
  protected considerReCallingForData (to: Route): boolean {
    const previous: ContentPreviousRoute = this.$store.getters['content/previous']

    if (previous.slug !== to.params.slug) {
      return true
    }

    if (Object.keys(previous.query).length === 0 && Object.keys(to.query).length === 0) {
      return false
    }

    const filteredPrev = { ...previous.query }
    const filteredNext = { ...to.query }

    for (const p of this.allowedQueryParams) {
      delete filteredPrev[p]
      delete filteredNext[p]
    }

    if (Object.keys(filteredPrev).length !== Object.keys(filteredNext).length) {
      return true
    }

    const arrayOfPrev: string[] = Object.entries(filteredPrev).map(([k, v]) => (`${k}: ${v}`))
    const arrayOfNext: string[] = Object.entries(filteredNext).map(([k, v]) => (`${k}: ${v}`))

    return difference(arrayOfPrev, arrayOfNext).length > 0 ||
      difference(arrayOfNext, arrayOfPrev).length > 0
  }
}
